import { COUNTRY_CODES, type CountryCode } from '@wix/form-multiline-address';
import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ComboBox as ComboBoxRAC,
  Input as InputRAC,
  Button as ButtonRAC,
  Popover as PopoverRAC,
  ListBox as ListBoxRAC,
  ListBoxItem as ListBoxItemRAC,
} from 'react-aria-components';

import { useConfig } from '../../../contexts/config-context';
import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useFieldAttributes } from '../../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { useTranslation } from '../../../contexts/translation-context';
import { type MlaCountryProps } from '../../../fields/mla-country/mla-country/mla-country-props';
import { useAddressLabel } from '../../../hooks/use-address-label';
import {
  applyInternalProps,
  findChildOfType,
} from '../../../services/find-child';
import { Description } from '../../field-description/field-description-headless';
import { Error } from '../../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../../field-label/field-label-headless';

export type CountryProps = {
  children: ReactNode;
};

export const Country = ({ children }: CountryProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
    options,
  } = useFieldPropsV2<MlaCountryProps>();

  const { t } = useTranslation();
  const { language } = useConfig();

  const countryOptions = useMemo(
    () =>
      (options || [])
        .reduce<{ value: string; label: string }[]>((result, option) => {
          if (option.value && COUNTRY_CODES.hasOwnProperty(option.value)) {
            result.push({
              value: option.value,
              label: t(
                `locale-dataset.countries.${COUNTRY_CODES[
                  option.value as CountryCode
                ].toUpperCase()}`,
              ),
            });
          }
          return result;
        }, [])
        .sort((a, b) => a.label.localeCompare(b.label, language)),
    [options, t, language],
  );

  const [inputValue, setInputValue] = useState('');

  const selectedLabel = useMemo(() => {
    if (!value) {
      return '';
    }
    const option = countryOptions.find((opt) => opt.value === value);
    return option?.label ?? '';
  }, [value, countryOptions]);

  const handleSelectionChange = useCallback(
    (key: React.Key | null) => {
      if (typeof key === 'string' && key !== '' && key !== value) {
        onChange(key as CountryCode);
        const option = countryOptions.find((opt) => opt.value === key);
        setInputValue(option?.label ?? '');
      }
    },
    [onChange, countryOptions, value],
  );

  const handleInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);
      if (newValue === '') {
        onChange(null as unknown as CountryCode);
      }
    },
    [onChange],
  );

  const countryLabel = useAddressLabel('country');
  const labelElement = findChildOfType(children, Country.Label);
  const controlElement = findChildOfType(children, Country.Control);
  const optionsElement = findChildOfType(children, Country.Options);
  const descriptionElement = findChildOfType(children, Country.Description);
  const errorElement = findChildOfType(children, Country.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, {
          label: countryLabel,
        })
      }
      renderInput={() => (
        <ComboBoxRAC
          selectedKey={value ?? null}
          inputValue={inputValue || selectedLabel}
          onSelectionChange={handleSelectionChange}
          onInputChange={handleInputChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
          menuTrigger="input"
        >
          {controlElement}
          {applyInternalProps<OptionsInternalProps>(optionsElement, {
            options: countryOptions,
          })}
        </ComboBoxRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type ControlProps = {
  children: ReactNode;
  className?: string;
};

const Control = ({ children, className }: ControlProps) => {
  const { disabled, hasError } = useFieldPropsV2<MlaCountryProps>();
  const inputElement = findChildOfType(children, Control.Input);
  const triggerElement = findChildOfType(children, Control.Trigger);

  return (
    <div
      className={className}
      data-disabled={disabled || undefined}
      data-invalid={hasError || undefined}
    >
      {inputElement}
      {triggerElement}
    </div>
  );
};

type InputProps = {
  className?: string;
};

const Input = ({ className }: InputProps) => {
  const { id } = useFieldPropsV2<MlaCountryProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();

  const inputRef = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    inputRef.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
    />
  );
};

type TriggerProps = {
  className?: string;
};

const Trigger = ({ className }: TriggerProps) => {
  return <ButtonRAC className={className} />;
};

Control.Input = Input;
Control.Trigger = Trigger;

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

type OptionsInternalProps = {
  options: { value: string; label: string }[];
};

const Options = ({ children, className, ...internal }: OptionsProps) => {
  const { options } = internal as OptionsInternalProps;

  const optionElement = findChildOfType(children, Options.Option);

  return (
    <PopoverRAC className={className}>
      <ListBoxRAC>
        {options.map((option) =>
          applyInternalProps<OptionInternalProps>(optionElement, {
            key: option.value,
            value: option.value,
            label: option.label,
          }),
        )}
      </ListBoxRAC>
    </PopoverRAC>
  );
};

type OptionProps = {
  className?: string;
};

type OptionInternalProps = {
  value: string;
  label: string;
};

const Option = ({ className, ...internal }: OptionProps) => {
  const { value, label } = internal as OptionInternalProps;
  return (
    <ListBoxItemRAC className={className} id={value} textValue={label}>
      {label}
    </ListBoxItemRAC>
  );
};

Options.Option = Option;

Country.Label = Label;
Country.Control = Control;
Country.Options = Options;
Country.Description = Description;
Country.Error = Error;
